import React from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useDataView } from "../../contexts/data.view.context";
import "../util/tables.css";
import { useConfirmedAddSubmit } from "../shared/useConfirmedAddSubmit";
import ConfirmAddDialog from "../util/confirmAddDialog";
import AddFormLayout from "../shared/AddFormLayout";
import SectionGrid from "../shared/SectionGrid";
import { useSectionedForm } from "../shared/useSectionedForm";

import { TextField } from "@mui/material";

const FORM_SECTIONS = [
  {
    id: "basic",
    label: "Basic info",
    fields: [
      "CONNECTION_ID",
      "CONNECTION_TYPE",
      "STATE",
      "PROJECT_ID",
      "PROJECT_OID",
      "PROJECT_NAME",
      "SPONSOR_NAME",
      "BUSINESS_ID",
    ],
  },
  {
    id: "connection",
    label: "Connection details",
    fields: [
      "CONNECTION_URL",
      "CONNECTION_PROVIDER",
      "PING_SERVICE",
      "CONNECTION_DATA",
    ],
  },
  {
    id: "auth",
    label: "Authentication",
    fields: ["AUTH_TYPE", "AUTH_ID", "AUTH_PIN", "AUTH_PASSCODE"],
  },
  {
    id: "limits",
    label: "Query limits",
    fields: ["MAX_ROWS_PER_QUERY", "MAX_QUERIES"],
  },
];

const EIConnectionForm = () => {
  const validationSchema = Yup.object().shape({
    CONNECTION_ID: Yup.string().required("Connection ID is required"),
    CONNECTION_TYPE: Yup.string().required("Connection ID is required"),
    PROJECT_ID: Yup.string().required("Project ID is required"),
    PROJECT_OID: Yup.string().required("Project OID is required"),
    PROJECT_NAME: Yup.string().required("Project Name is required"),
    SPONSOR_NAME: Yup.string().required("Sponsor Name is required"),
    BUSINESS_ID: Yup.string().required("Business ID is required"),
    CONNECTION_URL: Yup.string().required("Connection URL is required"),
    CONNECTION_PROVIDER: Yup.string().required(
      "Connection Provider is required"
    ),
    CONNECTION_DATA: Yup.string().required("Connection Data is required"),
    AUTH_TYPE: Yup.string().required("Auth Type is required"),
    AUTH_ID: Yup.string().required("Auth ID is required"),
    AUTH_PIN: Yup.string().required("Auth PIN is required"),
    AUTH_PASSCODE: Yup.string().required("Ath  passcode is required"),
    STATE: Yup.string().required("State is required"),
    MAX_ROWS_PER_QUERY: Yup.string().nullable().notRequired().default(""),
    MAX_QUERIES: Yup.string().nullable().notRequired().default(""),
    PING_SERVICE: Yup.string().nullable().notRequired().default(""),
  });

  const {
    register,
    handleSubmit,
    trigger,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const { sections, activeId, setActiveId, isFirst, isLast, goNext, goPrev } =
    useSectionedForm(FORM_SECTIONS, errors);

  const { fetchEIConenctionsFromAllEnv } = useDataView();
  const navigate = useNavigate();

  const { onSubmit, confirmDialogProps } = useConfirmedAddSubmit({
    resourcePath: "integrations/cfg/connections",
    notificationTitle: "Connection Entry",
    fetchFn: fetchEIConenctionsFromAllEnv,
    navigate,
    navigateTo: "/EIConnection",
  });

  const handleNext = async () => {
    const current = sections[sections.findIndex((s) => s.id === activeId)];
    const valid = await trigger(current.fields);
    if (valid) goNext();
  };

  return (
    <>
      <AddFormLayout
        title="Add EI connection"
        sections={sections}
        activeSectionId={activeId}
        onSectionChange={setActiveId}
        onBack={() => navigate(-1)}
        secondaryLabel={isFirst ? undefined : "Previous"}
        onSecondaryClick={goPrev}
        primaryLabel={isLast ? "Add" : "Next section"}
        onPrimaryClick={isLast ? handleSubmit(onSubmit) : handleNext}
      >
        {activeId === "basic" && (
          <SectionGrid>
            <TextField
              required
              label="Connection ID"
              size="small"
              fullWidth
              {...register("CONNECTION_ID")}
              error={!!errors.CONNECTION_ID}
              variant="outlined"
            />
            <TextField
              required
              label="Connection Type"
              size="small"
              fullWidth
              {...register("CONNECTION_TYPE")}
              error={!!errors.CONNECTION_TYPE}
              variant="outlined"
            />
            <TextField
              required
              label="State"
              size="small"
              fullWidth
              {...register("STATE")}
              error={!!errors.STATE}
              variant="outlined"
            />
            <TextField
              required
              label="Project ID"
              size="small"
              fullWidth
              {...register("PROJECT_ID")}
              error={!!errors.PROJECT_ID}
              variant="outlined"
            />
            <TextField
              required
              label="Project OID"
              size="small"
              fullWidth
              {...register("PROJECT_OID")}
              error={!!errors.PROJECT_OID}
              variant="outlined"
            />
            <TextField
              required
              label="Project Name"
              size="small"
              fullWidth
              {...register("PROJECT_NAME")}
              error={!!errors.PROJECT_NAME}
              variant="outlined"
            />
            <TextField
              required
              label="Sponsor Name"
              size="small"
              fullWidth
              {...register("SPONSOR_NAME")}
              error={!!errors.SPONSOR_NAME}
              variant="outlined"
            />
            <TextField
              required
              label="Business ID"
              size="small"
              fullWidth
              {...register("BUSINESS_ID")}
              error={!!errors.BUSINESS_ID}
              variant="outlined"
            />
          </SectionGrid>
        )}

        {activeId === "connection" && (
          <SectionGrid>
            <TextField
              required
              label="Connection URL"
              size="small"
              fullWidth
              {...register("CONNECTION_URL")}
              error={!!errors.CONNECTION_URL}
              variant="outlined"
            />
            <TextField
              required
              label="Connection Provider"
              size="small"
              fullWidth
              {...register("CONNECTION_PROVIDER")}
              error={!!errors.CONNECTION_PROVIDER}
              variant="outlined"
            />
            <TextField
              label="Ping Service"
              size="small"
              fullWidth
              {...register("PING_SERVICE")}
              error={!!errors.PING_SERVICE}
              variant="outlined"
            />
            <SectionGrid.FullWidth>
              <TextField
                required
                label="Connection Data"
                size="small"
                fullWidth
                {...register("CONNECTION_DATA")}
                error={!!errors.CONNECTION_DATA}
                variant="outlined"
                multiline
                rows={4}
              />
            </SectionGrid.FullWidth>
          </SectionGrid>
        )}

        {activeId === "auth" && (
          <SectionGrid>
            <TextField
              required
              label="Auth Type"
              size="small"
              fullWidth
              {...register("AUTH_TYPE")}
              error={!!errors.AUTH_TYPE}
              variant="outlined"
            />
            <TextField
              required
              label="Auth ID"
              size="small"
              fullWidth
              {...register("AUTH_ID")}
              error={!!errors.AUTH_ID}
              variant="outlined"
            />
            <TextField
              required
              label="Auth PIN"
              size="small"
              fullWidth
              {...register("AUTH_PIN")}
              error={!!errors.AUTH_PIN}
              variant="outlined"
            />
            <TextField
              required
              label="Auth Passcode"
              size="small"
              fullWidth
              {...register("AUTH_PASSCODE")}
              error={!!errors.AUTH_PASSCODE}
              variant="outlined"
            />
          </SectionGrid>
        )}

        {activeId === "limits" && (
          <SectionGrid>
            <TextField
              label="Max rows per query"
              size="small"
              fullWidth
              {...register("MAX_ROWS_PER_QUERY")}
              error={!!errors.MAX_ROWS_PER_QUERY}
              variant="outlined"
            />
            <TextField
              label="Max queries"
              size="small"
              fullWidth
              {...register("MAX_QUERIES")}
              error={!!errors.MAX_QUERIES}
              variant="outlined"
            />
          </SectionGrid>
        )}
      </AddFormLayout>

      <ConfirmAddDialog {...confirmDialogProps} itemLabel="connection" />
    </>
  );
};

export default EIConnectionForm;
